import {AdviserEquityPositionRecord} from "./AdviserEquityPositionRecord";
import {QuoteViewModel} from "../../quote/models/QuoteViewModel";
import {CodeAndExchange} from "../../instruments/types/CodeAndExchange";
import {Exchange} from "../../instruments/exchange/Exchange";
import {AccountName, AccountNumber} from "../../account/types/SimpleAccountTypes";

export type AdviserEquityPositionViewModels = ReadonlyArray<AdviserEquityPositionViewModel>;

export class AdviserEquityPositionViewModel implements CodeAndExchange {

    private constructor(private readonly record: AdviserEquityPositionRecord,
                        private readonly quote: QuoteViewModel | undefined) {
    }

    public get code(): string {
        return this.record.code;
    }

    public get exchange(): Exchange {
        return this.record.exchange;
    }

    public get org(): string | null {
        return this.record.org;
    }

    public get branch(): string | null {
        return this.record.branch;
    }

    public get adviser(): string | null {
        return this.record.adviser;
    }

    public get portfolioId(): number {
        return this.record.portfolioId;
    }

    public get accountNumber(): AccountNumber {
        return this.record.accountNumber;
    }

    public get accountName(): AccountName {
        return this.record.accountName;
    }

    public get quantity(): number {
        return this.record.quantity;
    }

    public get averagePrice(): number | null {
        return this.record.averagePrice;
    }

    public get previousClose(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.prevClose;
        }
    }

    public get open(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.open;
        }
    }

    public get last(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.last;
        }
    }

    public get marketValue(): number | null {
        if ((this.quote == null) || (this.quote.last == null)) {
            return null;
        }
        return this.quantity * this.quote.last;
    }

    public get pnl(): number | null {
        if ((this.quote == null) || (this.quote.last == null) || (this.averagePrice == null)) {
            return null;
        }
        const cost = this.averagePrice * this.quantity;
        const value = this.quantity * this.quote.last;
        return value - cost;
    }

    public get pnlPercent(): number | null {
        const last = this.last;
        if (last == null) {
            return null;
        } else if ((this.averagePrice == null) || (this.averagePrice === 0)) {
            return null;
        } else {
            const currentValue = this.quantity * last;
            const openingValue = this.quantity * this.averagePrice;
            const pnl = currentValue - openingValue;

            if (openingValue === 0) {
                return null;
            }

            return pnl / openingValue;
        }
    }

    public get todayPnl(): number | null {
        const previousClose = this.previousClose;
        const last = this.last;
        if ((previousClose == null) || (last == null)) {
            return 0;
        } else {
            const change = last - previousClose;
            return this.quantity * change;
        }
    }

    public get changePercent(): number | null {
        const previousClose = this.previousClose;
        const last = this.last;
        if ((previousClose == null) || (last == null) || (last === 0) || (previousClose === 0)) {
            return null;
        } else {
            const change = last - previousClose;
            return change / previousClose;
        }
    }

    public static of(record: AdviserEquityPositionRecord, quote?: QuoteViewModel) {
        return new AdviserEquityPositionViewModel(record, quote);
    }
}
